import type { ExperimentBundle, ExperimentVariantBundle, SignalContext } from "@tracking-web/shared";
// Desde el subpath PURO (no el barrel): así el SDK NO arrastra zod a su bundle. El valor del
// marcador es el mismo (fuente única en variantMarker.ts; lo re-exporta experiments.schema.ts).
import { utmVariantMarker } from "@tracking-web/shared/variantMarker";
import { evaluate, triggerFiredByTransient, triggerUsesBackIntent } from "./engine.js";
import { hashUnit } from "./hash.js";
import type { EmitFn } from "./adapters.js";

type Adapters = {
  apply: (action: ExperimentVariantBundle["action"], ruleId: string, ephemeral?: boolean) => boolean;
};

/**
 * Capa A/B en el navegador. Asignación 50/50 determinista por hash(anonId+expId)
 * — mismo visitante, misma variante, sin coste de servidor. Si el experimento ya
 * tiene ganador (status 'completed'), sirve esa variante al 100%. Aplica la acción
 * de la variante (control = noop), emite 'experiment_exposure' una vez, y atribuye
 * 'experiment_goal' cuando ocurre el objetivo (clic en el banner, o lectura).
 */
export function startExperiments(opts: {
  experiments: ExperimentBundle[];
  anonId: string;
  ctx: { state: SignalContext };
  adapters: Adapters;
  emit: EmitFn;
  /** Aplaza un experimento cuyo trigger aún no se puede decidir (p.ej. su condición usa el país y
   *  éste todavía no tiene veredicto). Devuelve true → NO exponer todavía (la exposición es un latch
   *  irreversible: exponer con country="" contaminaría la población del test). Ausente → nunca aplaza. */
  deferTrigger?: (trigger: NonNullable<ExperimentBundle["trigger"]>) => boolean;
}) {
  const { experiments, anonId, adapters, emit, ctx } = opts;
  const deferTrigger = opts.deferTrigger ?? (() => false);

  type Assigned = { exp: ExperimentBundle; variant: ExperimentVariantBundle };
  const assigned = new Map<string, Assigned>();
  const exposed = new Set<string>();
  const applied = new Set<string>();
  const goaled = new Set<string>();
  // Fase 4: pageUrl (normalizada) donde se EXPUSO cada experimento. Los objetivos solo
  // se atribuyen mientras el visitante siga en esa página → en publishers SPA evita
  // contaminar la tasa de conversión con interacciones de OTRAS páginas (un read_complete
  // o un element_click en /home no cuenta para un experimento expuesto en /pricing).
  const exposurePage = new Map<string, string>();
  const onExposurePage = (expId: string): boolean => ctx.state.pageUrl === exposurePage.get(expId);
  // Fase 4b: tiempo activo ACUMULADO en el momento de la exposición (baseline para medir
  // el time_on_page DESDE la exposición, no desde el boot) y experimentos cuyo objetivo
  // time_on_page ya quedó CONGELADO al salir de su página (no se reanuda al volver).
  const exposureActiveMs = new Map<string, number>();
  const timeSealed = new Set<string>();
  // Experimentos ya EXPUESTOS cuya variante aún no se ha PINTADO (DOM SPA sin montar):
  // se reintenta el pintado sin volver a exponer (la exposición es intención-de-tratar).
  const pendingAction = new Set<string>();
  const pendingAttempts = new Map<string, number>(); // intentos de pintado por experimento
  // Tope de reintentos: una variante cuyo ancla NO existe en esa ruta no debe reintentar
  // pintando (querySelector) en cada tick toda la sesión (CPU en miles de usuarios). Tras
  // el tope se desiste del pintado; el visitante SIGUE expuesto (ITT, conservador).
  const MAX_PAINT_RETRIES = 60;

  function pickVariant(exp: ExperimentBundle): ExperimentVariantBundle {
    if (exp.status === "completed" && exp.winnerVariantId) {
      const w = exp.variants.find((v) => v.id === exp.winnerVariantId);
      if (w) return w;
    }
    // Reparto ponderado: el hash [0,1) cae en el tramo proporcional al peso de
    // cada variante (pesos iguales = reparto uniforme). Sigue siendo determinista.
    const h = hashUnit(anonId + ":" + exp.id);
    const total = exp.variants.reduce((s, v) => s + (v.weight || 1), 0);
    let acc = 0;
    for (const v of exp.variants) {
      acc += v.weight || 1;
      if (h * total < acc) return v;
    }
    return exp.variants[exp.variants.length - 1];
  }

  function applyExperiment(a: Assigned) {
    if (applied.has(a.exp.id)) return;
    // EXPOSICIÓN = intención-de-tratar: ocurre al casar el trigger, en el MISMO instante
    // para control (noop) y tratamiento, SIN condicionarla a que la variante ya haya
    // pintado. Si la gateáramos al pintado, el control (pinta al instante) y el tratamiento
    // (pinta diferido en SPA) se expondrían en momentos distintos → sesgo de baseline en
    // time_on_page Y atrición diferencial (visitantes de tratamiento que navegan antes de
    // pintar no entrarían) → sesgo de conversión. Por eso exponemos ya y reintentamos SOLO
    // el pintado. Si la variante nunca llega a pintar, sigue contando como expuesta (ITT,
    // conservador y simétrico), no se cae de un solo brazo.
    applied.add(a.exp.id);
    exposurePage.set(a.exp.id, ctx.state.pageUrl); // Fase 4: página de exposición (scope de objetivos)
    exposureActiveMs.set(a.exp.id, ctx.state.activeReadingMs); // Fase 4b: baseline de tiempo
    exposed.add(a.exp.id);
    emit("experiment_exposure", { experimentId: a.exp.id, variantId: a.variant.id });
    if (
      a.variant.action.type !== "noop" &&
      !adapters.apply(a.variant.action, `exp:${a.exp.id}:${a.variant.id}`, triggerFiredByTransient(a.exp.trigger, ctx.state))
    ) {
      pendingAction.add(a.exp.id); // DOM aún sin montar → reintenta el PINTADO (no la exposición)
    }
  }

  /** Reintenta pintar la variante de experimentos ya expuestos cuyo DOM no estaba listo.
   *  Con tope de intentos para no quemar CPU si el ancla nunca aparece en esa ruta. */
  function applyPending() {
    for (const id of Array.from(pendingAction)) {
      const a = assigned.get(id);
      if (a && adapters.apply(a.variant.action, `exp:${id}:${a.variant.id}`, triggerFiredByTransient(a.exp.trigger, ctx.state))) {
        pendingAction.delete(id);
        pendingAttempts.delete(id);
        continue;
      }
      const n = (pendingAttempts.get(id) || 0) + 1;
      pendingAttempts.set(id, n);
      if (n >= MAX_PAINT_RETRIES) pendingAction.delete(id); // desiste; sigue expuesto (ITT)
    }
  }

  /**
   * Tests de "resultado externo" (rps): la variante NO se decide por hash sino por el
   * marcador que el redirect del servidor dejó en el utm_source de la landing. Aquí
   * solo pintamos el cambio en el DOM; no hay exposición ni objetivo (el revenue es
   * externo y las visitas se cuentan en el redirect). No entran en `assigned`.
   */
  function applyRpsFromUtm(exp: ExperimentBundle) {
    if (applied.has(exp.id)) return;
    let variant: ExperimentVariantBundle | undefined;
    // Completed: el publisher fijó ganador → se pinta a todo el tráfico marcado.
    if (exp.status === "completed" && exp.winnerVariantId) {
      variant = exp.variants.find((v) => v.id === exp.winnerVariantId);
    }
    if (!variant) {
      const stamp = exp.startStamp;
      const key = exp.key;
      if (!stamp || !key) return;
      let value = "";
      try {
        value = new URLSearchParams(location.search).get(key) || "";
      } catch {
        return;
      }
      if (!value) return; // sin marcador (tráfico orgánico) → no se aplica nada
      // El redirect REEMPLAZA el valor del parámetro key por el marcador → match exacto.
      variant = exp.variants.find((v, i) => value === utmVariantMarker(exp.id, v.name, i, stamp));
    }
    if (!variant) return;
    applied.add(exp.id);
    if (variant.action.type !== "noop") {
      adapters.apply(variant.action, `exp:${exp.id}:${variant.id}`, triggerFiredByTransient(exp.trigger, ctx.state));
    }
  }

  // Asignación inicial; aplica las que no dependen de un trigger.
  for (const exp of experiments) {
    if (exp.status === "stopped" || exp.status === "paused") continue;
    if (exp.goalType === "rps") {
      applyRpsFromUtm(exp);
      continue;
    }
    const a = { exp, variant: pickVariant(exp) };
    assigned.set(exp.id, a);
    if (!exp.trigger) applyExperiment(a);
  }

  /** Reevalúa los experimentos con trigger en cada cambio de señal, contra la pageUrl
   *  VIVA (Fase 4): un experimento acotado por URL se expone al navegar a esa página en
   *  SPA, igual que en MPA. La atribución de objetivos se acota a la página de exposición
   *  (onExposurePage) para que descongelar no contamine la tasa de conversión. */
  function tick(state: SignalContext) {
    if (pendingAction.size) applyPending(); // reintenta el pintado de variantes ya expuestas
    for (const a of assigned.values()) {
      if (applied.has(a.exp.id) || !a.exp.trigger) continue;
      if (deferTrigger(a.exp.trigger)) continue; // país sin veredicto aún → no exponer todavía
      if (evaluate(a.exp.trigger, state)) applyExperiment(a);
    }
    evaluateReadingProgressGoals(state);
  }

  /**
   * Objetivo `reading_progress`: se evalúa contra el ESTADO CONTINUO de lectura
   * (`state.readingProgressPct`), NO contra el evento `reading_progress` emitido. Así el
   * coalescing de la EMISIÓN a deciles (collectors/reading.ts) NO altera el umbral de
   * conversión: `tick` corre en cada cambio de señal (`ctx.touch()` en cada `measure()`, ~1%),
   * de modo que el % se observa con granularidad fina aunque solo se EMITA/persista 1 fila por
   * decil. Mismos guardas que la atribución binaria de handleEvent:
   *  - solo experimentos EXPUESTOS y aún no convertidos (`goaled` → one-shot, sin doble conteo);
   *  - solo en la página de exposición (Fase 4, `onExposurePage`): en SPA un avance de lectura en
   *    OTRA página no atribuye al experimento expuesto en /pricing.
   */
  function evaluateReadingProgressGoals(state: SignalContext) {
    for (const a of assigned.values()) {
      if (a.exp.goalType !== "reading_progress") continue;
      if (!exposed.has(a.exp.id) || goaled.has(a.exp.id)) continue;
      if (!onExposurePage(a.exp.id)) continue;
      if (state.readingProgressPct >= (a.exp.goalThreshold ?? 50)) {
        goaled.add(a.exp.id);
        emit("experiment_goal", { experimentId: a.exp.id, variantId: a.variant.id, goalType: a.exp.goalType });
      }
    }
  }

  /** ¿Un Atrás AHORA pintaría algún experimento? El back-guard lo consulta para no armar un centinela
   *  que no llegaría a pintar nada. Respeta el gating REAL de la capa A/B: solo experimentos asignados
   *  (status running/completed; los paused/stopped no entran en `assigned`), NO ya expuestos/pintados,
   *  con variante que SÍ pinta (no control/noop), cuyo trigger usa Atrás y se cumpliría con Atrás. */
  function armableOnBack(state: SignalContext): boolean {
    for (const a of assigned.values()) {
      if (applied.has(a.exp.id)) continue; // ya expuesto → no re-pinta (applied persiste entre páginas)
      if (a.variant.action.type === "noop") continue; // control → no pinta nada
      const t = a.exp.trigger;
      if (t && triggerUsesBackIntent(t) && evaluate(t, { ...state, backExitIntent: true })) return true;
    }
    return false;
  }

  /** Atribuye el objetivo. index lo llama por cada evento emitido. */
  function handleEvent(type: string, props?: Record<string, unknown>) {
    if (type.indexOf("experiment_") === 0) return; // evitar bucle
    for (const a of assigned.values()) {
      if (!exposed.has(a.exp.id)) continue;
      if (!onExposurePage(a.exp.id)) {
        // Fase 4b: salió de la página de exposición → CONGELA el time_on_page (no se
        // reanuda al volver; el tiempo de otras páginas no debe atribuirse a este test).
        if (a.exp.goalType === "time_on_page") timeSealed.add(a.exp.id);
        continue; // Fase 4: objetivos solo en la página de exposición
      }
      const g = a.exp.goalType;

      // Objetivo continuo: el tiempo activo DESDE LA EXPOSICIÓN (no desde el boot). Se
      // emite en page_exit Y en cada heartbeat active_time. El servidor se queda con el
      // MÁXIMO por visitante: emitir de más es idempotente (mismo n, media y varianza),
      // pero si el page_exit final se pierde (móvil/bfcache) el último heartbeat ya
      // entregó casi todo el tiempo → el veredicto solo mejora al recuperar datos, nunca
      // cambia por reenvío. NO se bloquea con `goaled` (el valor sube y queremos el máx).
      if (g === "time_on_page") {
        if (timeSealed.has(a.exp.id)) continue; // ya congelado al salir de la página
        if (type === "page_exit" || type === "active_time") {
          // Tiempo activo acumulado DESDE la exposición: un experimento expuesto en una
          // página intermedia no hereda el tiempo de las páginas anteriores.
          const value = Math.max(0, (Number(props?.activeReadingMs) || 0) - (exposureActiveMs.get(a.exp.id) ?? 0));
          emit("experiment_goal", {
            experimentId: a.exp.id,
            variantId: a.variant.id,
            goalType: g,
            value,
          });
        }
        continue;
      }

      if (goaled.has(a.exp.id)) continue;
      let hit = false;
      if (g === "cta_click" && type === "adaptation_click") {
        // Un test de clic solo convierte si el clic fue sobre algo clicable (cta=true):
        // cerrar el popup o clicar un hueco del banner no es una conversión.
        hit = props?.ruleId === `exp:${a.exp.id}:${a.variant.id}` && props?.cta === true;
      } else if (g === "read_complete" && type === "read_complete") {
        hit = true;
      }
      // OJO: el objetivo `reading_progress` NO se evalúa aquí. Se mueve a `tick` (estado
      // continuo) para que el coalescing de la EMISIÓN a deciles no cambie su umbral de
      // conversión. Ver evaluateReadingProgressGoals abajo.
      if (hit) {
        goaled.add(a.exp.id);
        emit("experiment_goal", {
          experimentId: a.exp.id,
          variantId: a.variant.id,
          goalType: g,
        });
      }
    }
  }

  // Objetivo 'element_click': clic en un elemento que YA EXISTE en la página del
  // publisher (selector CSS del cliente), no en nuestro banner inyectado. No se
  // puede resolver en handleEvent porque ahí no tenemos el elemento del DOM (el
  // 'selector' del evento click es uno simplificado y no casaría). Por eso un
  // listener propio en captura que prueba target.closest(goalSelector).
  function onElementClick(e: Event) {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    for (const a of assigned.values()) {
      if (a.exp.goalType !== "element_click") continue;
      if (!exposed.has(a.exp.id) || goaled.has(a.exp.id) || !a.exp.goalSelector) continue;
      if (!onExposurePage(a.exp.id)) continue; // Fase 4: solo en la página de exposición
      let hit: Element | null = null;
      try {
        hit = target.closest(a.exp.goalSelector); // selector inválido → catch → no cuenta
      } catch {
        hit = null;
      }
      if (hit) {
        goaled.add(a.exp.id);
        emit("experiment_goal", {
          experimentId: a.exp.id,
          variantId: a.variant.id,
          goalType: a.exp.goalType,
        });
      }
    }
  }
  document.addEventListener("click", onElementClick, { capture: true, passive: true });

  return { tick, handleEvent, armableOnBack };
}
